import { t } from "i18next";
import { ng } from "@common/ngds";
import { addAlert, removeAlert } from "../ui/uiSlice";

/*
Second order analysis needs a normal force to act on.

The magnification a second order run produces is driven entirely by the axial force acting through
the lateral deflection. A load combination that carries no normal force therefore gets no second
order effect at all: its results are identical to the first order ones and its N diagram is a flat
zero line. That is easy to set up by accident - a combination whose only load case is a horizontal
point load, or one where the gamma factor on the vertical load case has been left at zero - and the
symptom (nothing changed when 2nd order was switched on) does not look like a modelling mistake.

The normal force is taken to be the vertical point load, and only that. Self weight also acts along
the column, but it is not what a user reaches for when they mean "the load on the column": a model
carrying nothing but its own weight is exactly the case this warning exists to catch, so a self
weight load case does not satisfy the check.

The condition is read from the load *input* rather than from the results, but it is checked at two
moments rather than continuously: when 2nd order analysis is switched on, and when the Analysis
button is pressed. Those are the two points where the user has said they want a second order
result, so they are the two points where being told they will not get one is useful - checking on
every keystroke would raise the warning while a model is still half entered. Both entry points call
checkSecondOrderVerticalPointLoad; it raises one summarising alert naming every offending
combination, because with generated combinations there can be dozens sharing one cause and one fix.
*/

/** Fixed id, so the single summarising alert can be replaced and retracted without bookkeeping. */
const ALERT_ID = "second-order-no-vertical-point-load";

/**
 * Alerts raised from the load input rather than from a calculation. Unlike "calc" these are not
 * retracted by calcRunStarted: this one is raised as a run starts, so clearing it there would
 * remove it before it had been read.
 */
const ALERT_SOURCE = "input";

type StructureJson = ng.structure.Data_json | null | undefined;

/** Just enough of a dispatch to raise and retract this alert - see the note in calcWarnings. */
type AlertDispatch = (action: ReturnType<typeof addAlert> | ReturnType<typeof removeAlert>) => void;

// A proto enum survives the json round trip as its name, but a caller holding a real message has
// the number instead. The generated *_FromObject converters take both; they log for anything else,
// so an unset field is answered here rather than passed on.
const loadType = (value: string | undefined): ng.load.Type =>
  value === undefined ? ng.load.Type.TYPE_UNSPECIFIED : ng.load.Type_FromObject(value);

const distributionType = (value: string | undefined): ng.load.DistributionType =>
  value === undefined
    ? ng.load.DistributionType.DISTRIBUTION_TYPE_UNSPECIFIED
    : ng.load.DistributionType_FromObject(value);

const coefficientType = (value: string | undefined): ng.loadcombination.CoefficientType =>
  value === undefined
    ? ng.loadcombination.CoefficientType.COEFFICIENT_TYPE_UNSPECIFIED
    : ng.loadcombination.CoefficientType_FromObject(value);

/** JsonNumber is a number or its string form, depending on how the value round-tripped. */
const isNonZero = (value: unknown): boolean => {
  const n = Number(value);
  return Number.isFinite(n) && n !== 0;
};

/**
 * Whether this load is a vertical point load - the axial force SXInputLoads creates from the
 * "Vertical" table.
 *
 * It is the only point load with an x component in its direction: a horizontal point load acts in
 * +Y or +Z with x = 0, and a line load is a line load. Nothing else in the input qualifies, self
 * weight included - its generated line loads act along the column but are deliberately not counted
 * here (see the note at the top of this file).
 */
const isVerticalPointLoad = (load: ng.load.Data_json): boolean =>
  loadType(load.type) === ng.load.Type.TYPE_FORCE &&
  distributionType(load.distribution) === ng.load.DistributionType.DISTRIBUTION_TYPE_POINT &&
  isNonZero(load.direction?.x) &&
  // A load defined with a magnitude of zero is a load that is not there.
  (load.values ?? []).some(isNonZero);

/**
 * The partial coefficient a combination applies to one of its load cases. Mirrors getLoadcaseFactor
 * in @common/helpers/protoGen: gamma is the factor a combination is built from, and it falls back
 * to the first coefficient because that is what SXInputLoadComboPart reads and writes when a
 * combination carries only one.
 */
const gammaFactor = (part: ng.loadcombination.CombinationPart_json): number => {
  const coefficients = part.coefficients ?? [];
  const gamma = coefficients.find(
    (c) => coefficientType(c.type) === ng.loadcombination.CoefficientType.COEFFICIENT_TYPE_GAMMA,
  );
  return Number((gamma ?? coefficients[0])?.value ?? 0);
};

/**
 * The names of the load combinations that are analysed but include no vertical point load, in the
 * order they are defined. Deactivated combinations are skipped - they are left out of the analysis,
 * so a warning about one would be a warning about nothing.
 *
 * Pure, and exported separately so it can be tested without a store.
 */
export const combinationsWithoutVerticalPointLoad = (structure: StructureJson): string[] => {
  const loading = structure?.loading;
  if (!loading) return [];

  // Which load cases carry a vertical point load - resolved once, then reused by every combination.
  const axialCaseGuids = new Set<string>();
  for (const load of loading.loads ?? []) {
    const guid = load.lcaseGuid;
    if (!guid || axialCaseGuids.has(guid)) continue;
    if (isVerticalPointLoad(load)) axialCaseGuids.add(guid);
  }

  const without: string[] = [];
  for (const combination of loading.combinations ?? []) {
    if (combination.deactive) continue;

    // A load case included with a gamma of zero contributes nothing, so it cannot supply the normal
    // force either - that is one of the ways this situation is reached by accident.
    const hasVerticalPointLoad = (combination.parts ?? []).some(
      (part) => part.lcaseGuid !== undefined && axialCaseGuids.has(part.lcaseGuid) && gammaFactor(part) !== 0,
    );
    if (hasVerticalPointLoad) continue;

    without.push(combination.id?.name || combination.id?.guid || "");
  }

  return without;
};

/** The localised summary, or null when there is nothing to warn about. */
export const secondOrderNoVerticalPointLoadMessage = (
  structure: StructureJson,
  secondOrder: boolean,
): string | null => {
  if (!secondOrder) return null;

  const names = combinationsWithoutVerticalPointLoad(structure);
  if (names.length === 0) return null;

  return t("warn_second_order_no_vertical_point_load_.label", {
    defaultValue:
      "2nd order analysis is active, but these load combinations contain no vertical point load: {{combinations}}.",
    combinations: names.join(", "),
    count: names.length,
  });
};

/**
 * Raise, replace or retract the summarising warning. Called when 2nd order analysis is switched on
 * (SXInputCalculation) and when the Analysis button is pressed (App).
 *
 * The previous alert is always retracted first, so switching 2nd order off, or fixing the loads and
 * running again, takes the warning down rather than leaving a stale list of combinations up. It
 * also makes the alert re-raisable: addAlert suppresses duplicate text, so an identical message
 * that the user dismissed could not be shown again otherwise.
 *
 * The alert is sticky (`keep`) because it reports a modelling mistake, which stays wrong until it
 * is fixed, rather than a momentary event.
 */
export const checkSecondOrderVerticalPointLoad = (
  structure: StructureJson,
  secondOrder: boolean,
  dispatch: AlertDispatch,
): void => {
  dispatch(removeAlert(ALERT_ID));

  const message = secondOrderNoVerticalPointLoadMessage(structure, secondOrder);
  if (message === null) return;

  dispatch(
    addAlert({
      id: ALERT_ID,
      type: "warning",
      status: t("warning_.label", "Warning"),
      context: undefined,
      text: message,
      keep: true,
      source: ALERT_SOURCE,
    }),
  );
};

/*
2nd order analysis of a reinforced concrete column without cracking is not the general method.

With "Consider cracking during analysis" off, the engine keeps every section uncracked: the
stiffness is the creep-reduced transformed section, constant and independent of the moment. The
analysis then has geometric second order but linear material behaviour. EN 1992-1-1 5.8.6, which
the "General method" label promises, requires non-linear material behaviour (5.8.6(1) via 5.7,
the Eq. 3.14 curve of 5.8.6(3)), so the second-order moments and deflections come out too small,
increasingly so with slenderness. Measured on a 4 m column: deflection -30 %, M_Ed -1.1 %
(ms-structural/sx-column#418).

So for a concrete column cracking follows 2nd order (SXInputCalculation): switching 2nd order on
switches cracking on and locks it, switching 2nd order off switches cracking off again but leaves it
free, since 1st order runs either way. That combination cannot be reached from the UI any more, so
nothing checks for it; projects stored before the lock are not repaired, as none were released.
Each change this makes to the user's input is announced here.
*/

/** Fixed id, so switching 2nd order back and forth replaces the notice rather than stacking it. */
const CRACKING_FOLLOWS_ALERT_ID = "cracking-follows-second-order";

/**
 * Tell the user that switching 2nd order also changed "Consider cracking during analysis". Call it
 * only when cracking actually changed - turning 2nd order on with cracking already on is no news.
 * Not sticky: it reports something done, not something to fix.
 */
export const announceCrackingFollowsSecondOrder = (crackingOn: boolean, dispatch: AlertDispatch): void => {
  dispatch(removeAlert(CRACKING_FOLLOWS_ALERT_ID));
  dispatch(
    addAlert({
      id: CRACKING_FOLLOWS_ALERT_ID,
      type: "info",
      status: t("information_.label", "Information"),
      context: undefined,
      text: crackingOn
        ? t("info_cracking_switched_on_.label", {
          defaultValue:
            '"Consider cracking during analysis" has been switched on, since 2nd order analysis of a concrete column requires it. It cannot be switched off while 2nd order analysis is on.',
        })
        : t("info_cracking_switched_off_.label", {
          defaultValue:
            '"Consider cracking during analysis" has been switched off, since 2nd order analysis was switched off. It can be switched on again for a 1st order analysis.',
        }),
      source: ALERT_SOURCE,
    }),
  );
};
